/*
 * Smores collection — client-side tab filter & sort
 *
 * Each tab carries a `data-url` (the collection or tag URL). On click
 * we fetch that URL, parse the HTML, and swap the products grid +
 * count text in place. No section-rendering API; the destination page
 * just needs to also render a `.smores-collection__products` grid
 * (which it does, since the section is enabled on collection / list-
 * collections / search templates).
 *
 * Fallback path: if the fetch fails OR the destination page doesn't
 * have our grid markup, we navigate to the URL normally so the user
 * still sees the filtered products.
 */
class SmoresCollection extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-smores-tab]')];
    this.sort = this.querySelector('[data-smores-sort]');
    this.grid = this.querySelector('.smores-collection__products');
    this.count = this.querySelector('[data-smores-count]');

    this.tabs.forEach((tab) => tab.addEventListener('click', (e) => this.onTab(e, tab)));
    if (this.sort) this.sort.addEventListener('change', () => this.onSort());

    window.addEventListener('popstate', () => this.syncFromUrl());
  }

  onTab(e, tab) {
    const url = tab.dataset.url;
    if (!url) {
      // Tab has no URL configured — let it fall through to whatever
      // href is on the element. If no href either, do nothing.
      if (!tab.getAttribute('href')) e.preventDefault();
      return;
    }
    e.preventDefault();
    this.setActiveTab(tab);
    this.load(url, true);
  }

  onSort() {
    const activeTab = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
    const base = activeTab?.dataset.url || window.location.pathname;
    const sortKey = this.sort.value;
    const sep = base.includes('?') ? '&' : '?';
    const target = sortKey ? `${base}${sep}sort_by=${encodeURIComponent(sortKey)}` : base;
    this.load(target, true);
  }

  setActiveTab(tab) {
    this.tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
  }

  syncFromUrl() {
    const here = window.location.pathname + window.location.search;
    const match = this.tabs.find((t) => t.dataset.url === here);
    if (match) this.setActiveTab(match);
  }

  async load(url, pushHistory) {
    if (!this.grid) return;
    this.grid.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('.smores-collection__products');
      const newCount = doc.querySelector('[data-smores-count]');

      if (!newGrid) {
        // Destination page doesn't render our section — fall back to a
        // full navigation so the user still gets the filtered products.
        window.location.href = url;
        return;
      }

      this.grid.innerHTML = newGrid.innerHTML;
      if (newCount && this.count) this.count.textContent = newCount.textContent;

      if (pushHistory) {
        history.pushState({ smoresCollection: url }, '', url);
      }
    } catch (err) {
      console.error('[smores-collection] AJAX swap failed, falling back to navigation', err);
      window.location.href = url;
    } finally {
      this.grid.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('smores-collection')) {
  customElements.define('smores-collection', SmoresCollection);
}